import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    title: "Ordering & Shipping",
    items: [
      { q: "How fast do orders ship?", a: "Orders placed before 3pm EST ship the same business day. All others ship within 24 hours from our American facility, Monday through Friday." },
      { q: "Is shipping discreet?", a: "Yes. All shipments are sent in plain, unmarked packaging with no exterior labels referencing the contents. Orders are tracked end-to-end." },
      { q: "Do you ship internationally?", a: "Yes. International shipping is available at checkout. Customers are responsible for any customs duties or import fees levied by their country." },
      { q: "When is shipping free?", a: "Free U.S. shipping is automatically applied on orders over $200. Below that, flat-rate domestic shipping is $14.99." },
      { q: "How is product temperature controlled?", a: "Compounds that require cold-chain handling are packed with insulated liners and ice packs. Tracked delivery means your order spends minimal time in transit." },
    ],
  },
  {
    title: "Product & Quality",
    items: [
      { q: "Are your compounds third-party tested?", a: "Every batch is independently HPLC tested for identity and purity. A Certificate of Analysis is on file for each lot we produce." },
      { q: "Where can I find a Certificate of Analysis (COA)?", a: "On every product detail page you will see a COA link. If your product page shows 'COA — Coming Soon', that batch's COA is being finalized — email support and we'll send it directly." },
      { q: "What is the typical purity?", a: "Our standard target is >99% peptide purity. Batches that fall below 98% are rejected and never reach customers." },
      { q: "Are your products FDA approved?", a: "No. All compounds are sold strictly FOR LABORATORY RESEARCH USE ONLY. They are not intended for human or animal consumption and have not been evaluated by the FDA." },
    ],
  },
  {
    title: "Reconstitution & Use",
    items: [
      { q: "What is bacteriostatic water (B-H20)?", a: "B-H20 is sterile water preserved with 0.9% benzyl alcohol, used for reconstituting lyophilized research compounds. It maintains stability after reconstitution and prevents microbial growth." },
      { q: "How should I store unopened vials?", a: "Lyophilized peptides are best stored in a refrigerator (2–8°C) or freezer (-20°C) for long-term stability. Always avoid direct light and heat." },
      { q: "Once reconstituted, how long is it stable?", a: "Stability varies by compound. As a general guideline for research storage: 1–2 weeks refrigerated for most peptides. Refer to your batch COA for compound-specific guidance." },
    ],
  },
  {
    title: "Account & Payment",
    items: [
      { q: "What payment methods do you accept?", a: "Major credit cards (Visa, Mastercard, American Express, Discover), Apple Pay, and Google Pay. Payments are processed via Stripe with 256-bit SSL encryption." },
      { q: "Do I need an account to order?", a: "No. Guest checkout is available. Provide your email and we'll send a receipt and tracking automatically." },
      { q: "Where can I apply a coupon?", a: "Coupon codes are applied during checkout in the order summary panel." },
    ],
  },
  {
    title: "Returns & Support",
    items: [
      { q: "What is your return policy?", a: "Due to the nature of research compounds, we cannot accept returns once a vial has been opened. If your order arrives damaged or you receive the wrong product, contact support within 7 days for a replacement or full refund." },
      { q: "How do I track my order?", a: "After your order ships, a tracking number is emailed to you. You can also use the order number from your confirmation page to look up your order status." },
      { q: "How do I contact support?", a: "Visit our Contact page or email us directly. We respond to every inquiry within 24 hours, Monday through Friday." },
    ],
  },
];

const FAQ = () => {
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-10 py-20" data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions"
        description="Answers about ordering, shipping, COAs, lab testing, returns, and compliance at Certified Wellness."
        canonical="/faq"
      />
      <div className="max-w-3xl mb-16">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Knowledge Base</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
          Frequently asked <span className="font-serif italic font-normal">questions.</span>
        </h1>
        <p className="text-gray-500 mt-5 text-[17px] font-light leading-relaxed max-w-2xl">
          Everything you need to know about ordering, lab testing, and using research compounds responsibly.
        </p>
      </div>

      <div className="space-y-12">
        {SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="font-display text-2xl font-semibold text-[#0a0a0a] mb-5 pb-3 border-b border-gray-200">
              {section.title}
            </h2>
            <div className="divide-y divide-gray-100 border-b border-gray-100">
              {section.items.map((item, idx) => {
                const k = `${section.title}-${idx}`;
                const isOpen = !!open[k];
                return (
                  <div key={k}>
                    <button
                      onClick={() => toggle(k)}
                      className="w-full flex items-center justify-between text-left py-5 hover:text-[#b91c1c] transition-colors"
                      data-testid={`faq-toggle-${section.title.split(" ")[0].toLowerCase()}-${idx}`}
                    >
                      <span className="font-medium text-[#0a0a0a] pr-6">{item.q}</span>
                      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180 text-[#b91c1c]" : "text-gray-400"}`} />
                    </button>
                    {isOpen && (
                      <p className="pb-6 text-gray-600 leading-relaxed font-light max-w-3xl">{item.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-20 bg-[#0a0a0a] text-white p-10 text-center">
        <div className="text-[10px] font-semibold tracking-[0.32em] uppercase text-red-400 mb-3">Still have questions?</div>
        <h3 className="font-display text-3xl font-semibold mb-4">Our support team responds within 24 hours.</h3>
        <a href="/contact" className="inline-block bg-white text-[#0a0a0a] px-8 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] hover:bg-red-400 hover:text-white transition-colors">
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default FAQ;
